import { GoogleTagManager, tracking } from '@iwoca/frontend-tracking-library';
import { stringify } from 'qs';
import { useNavigate } from 'react-router';

import { trackLeadQuality } from './trackLeadQuality';
import { fetchPutAndGetState } from '../../api/lendingApi/lendingApiMutations';
import { useGetStateQuery } from '../../api/lendingApi/lendingApiQueries';
import { getQueryParams } from '../../helpers/getQueryParams';
import {
  TGetStateResponseDE,
  TPutStateRequestBodyDE,
} from '../../lapiOverrideTypesDE';
import { TSignupStepsDE } from '../../types';
import { ACCOUNT_PAGES_URL } from '../../urls';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import logger from '../../utils/logger';
import { useCurrentStep } from '../useCurrentStep';
import { useSignupParams } from '../useSignupParams';
import { useImpersonatedStateKey } from '../useStateKey';

export const useSignupRouterDE = () => {
  const { rawAffiliateParam } = useSignupParams();
  const navigate = useNavigate();

  const { state } = useGetStateQuery<TGetStateResponseDE>();

  const basePath = `/${rawAffiliateParam}`;

  const currentStep = useCurrentStep();
  const impersonatedStateKey = useImpersonatedStateKey();

  const navigateSignupDE = async (
    newRoute: TSignupStepsDE,
    stateKey?: string,
    updatedState?: TPutStateRequestBodyDE['data'],
  ) => {
    if (!stateKey) {
      return logger.error(
        'Attempted to navigate without stateKey. StateKey is required for the PUT to state.',
      );
    }

    const newState = updatedState || state;

    if (newRoute !== 'neu' && newState.ui?.activePartnerStep !== newRoute) {
      const putStateBody: TPutStateRequestBodyDE = {
        data: {
          ...newState,
          ui: {
            ...newState.ui,
            activePartnerStep: newRoute,
          },
        },
      };

      await fetchPutAndGetState({ body: putStateBody, stateKey });
    }

    const newQueryParams = {
      ...getQueryParams(),
    };

    if (newRoute !== 'neu' && newQueryParams.amount) {
      delete newQueryParams.amount;
    }

    if (impersonatedStateKey) {
      newQueryParams.state_key = impersonatedStateKey;
    }

    GoogleTagManager.track('setLatestActiveSignupStep', {
      latestActiveSignupStep: getStepNumber(currentStep as TSignupStepsDE),
      isImpersonating: !!impersonatedStateKey,
    });

    if (currentStep === 'schritt-1' && newRoute === 'schritt-2') {
      await trackLeadQuality();
    }

    let newPath = `${newRoute}/`;
    if (stringify(newQueryParams)) {
      newPath = `${newRoute}/?${stringify(newQueryParams)}`;
    }

    navigate(`${basePath}/${newPath}`);
  };

  const navigateToAccountPages = async (stateKey: string) => {
    if (state.ui?.activeStep?.id !== 'myAccount') {
      const putStateBody: TPutStateRequestBodyDE = {
        data: {
          ...state,
          ui: {
            ...state.ui,
            activeStep: {
              id: 'myAccount',
            },
          },
        },
      };

      await fetchPutAndGetState({ body: putStateBody, stateKey });
    }

    tracking.ampli.signupCompleted(getAmplitudeDefaultEventProperties());

    window.location.href = ACCOUNT_PAGES_URL;
  };

  return {
    basePath,
    currentStep,
    navigateSignupDE,
    navigateToAccountPages,
  };
};

const getStepNumber = (currentStep: TSignupStepsDE): number => {
  switch (currentStep) {
    case 'neu':
      return 1;
    case 'schritt-1':
      return 2;
    case 'schritt-2':
      return 3;
    case 'angebote':
      return 4;
    case 'eignungscheck':
      return 5;
    case 'schritt-3':
      return 6;
    default:
      return 1;
  }
};
